import { useQuery } from '@tanstack/react-query';
import laurine from 'assets/laurine.jpg';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ApiRegisteredCaregiversManager from 'services/api/ApiRegisteredCaregiversManager';
import { RegisteredCaregiverDTO } from 'services/api/models/RegisteredCaregiverDTO';
import { UserDTO } from 'services/api/models/UserDTO';
import { selectCurrentUser } from 'store/users/selectors';

/** What the messaging screens show of a caregiver: her name, photo and job. */
export type CaregiverIdentity = {
  /** The caregiver's user id; none for the default referent shown before any conversation. */
  id?: string;
  firstName: string;
  /** First name and initial, e.g. « Laurine M. ». */
  name: string;
  photoUrl?: string;
  /** The caregiver's first group, e.g. « Aide-soignante » (undefined while it loads). */
  role?: string;
};

export type QualificationLevel = 'medical' | 'paramedical' | 'alternative_medicine';
export const QUALIFICATION_LEVELS: QualificationLevel[] = ['medical', 'paramedical', 'alternative_medicine'];

/** Caregivers are sorted by their first group, as everywhere else in the app. */
export function qualificationLevelOf(caregiver: RegisteredCaregiverDTO): QualificationLevel {
  return (caregiver.caregiver.caregiver_groups[0]?.qualification_level as QualificationLevel) || 'paramedical';
}

export function shortName(firstName?: string, lastName?: string, fallback = '') {
  if (!firstName) return fallback;
  return lastName ? `${firstName} ${lastName[0]}.` : firstName;
}

export function useRegisteredCaregivers() {
  return useQuery({
    queryKey: ['registered-caregivers'],
    queryFn: () => ApiRegisteredCaregiversManager.getRegisteredCaregiversList(),
    staleTime: 1000 * 60 * 10,
  });
}

// Drawn once per app session, so the caregivers shown stay put while she browses
const sessionSeed = Math.floor(Math.random() * 2 ** 32);

/** A caregiver's place in this session's random order (FNV-1a of her id, salted with the seed). */
function sessionRank(id: string) {
  let hash = 2166136261 ^ sessionSeed;
  for (let i = 0; i < id.length; i++) {
    hash = Math.imul(hash ^ id.charCodeAt(i), 16777619);
  }
  return hash >>> 0;
}

/** Up to `count` caregivers at random, never two of the same specialty (their first group). */
export function pickCaregivers(caregivers: RegisteredCaregiverDTO[], count: number) {
  const specialties = new Set<string>();
  const picked: RegisteredCaregiverDTO[] = [];
  for (const caregiver of [...caregivers].sort((a, b) => sessionRank(a.user.id) - sessionRank(b.user.id))) {
    const specialty = caregiver.caregiver.caregiver_groups[0]?.id ?? caregiver.user.id;
    if (specialties.has(specialty)) continue;
    specialties.add(specialty);
    picked.push(caregiver);
    if (picked.length === count) break;
  }
  return picked;
}

export function useRegisteredCaregiver(caregiverId?: string) {
  return useQuery({
    queryKey: ['registered-caregivers', caregiverId],
    queryFn: () => ApiRegisteredCaregiversManager.getRegisteredCaregiver({ caregiverId: caregiverId! }),
    enabled: Boolean(caregiverId),
    staleTime: 1000 * 60 * 10,
  });
}

/** A conversation's caregiver as the messaging screens show her, her job fetched from her profile. */
export function useCaregiverIdentity(user?: UserDTO): CaregiverIdentity | undefined {
  const { data: profile } = useRegisteredCaregiver(user?.id);
  return user && caregiverIdentity(user, profile);
}

function caregiverIdentity(user: UserDTO, profile?: RegisteredCaregiverDTO): CaregiverIdentity {
  // The caregiver profile holds her name as patients know it
  const firstName = profile?.caregiver.first_name || user.first_name;
  const lastName = profile?.caregiver.first_name ? profile.caregiver.last_name : user.last_name;
  return {
    id: user.id,
    firstName: firstName || user.display_name,
    name: shortName(firstName, lastName, user.display_name),
    photoUrl: user.photo_url || undefined,
    role: profile?.caregiver.caregiver_groups[0]?.display_name,
  };
}

/**
 * The patient's referring caregiver (`referent_caregiver`): the first-line
 * caregiver the backend picked for her, then whoever picked up her first-line
 * conversations. Without one (no first-line caregiver was available), the
 * default referent the onboarding introduces, Laurine.
 */
export function useReferentCaregiver(): { referent: CaregiverIdentity; isDefault: boolean } {
  const { t } = useTranslation();
  // Her whole profile comes with her account, so she is introduced from the first render
  const referentProfile = useSelector(selectCurrentUser)?.referent_caregiver;

  if (!referentProfile) {
    return {
      referent: {
        firstName: t('messagingV3.defaultReferent.firstName'),
        name: t('messagingV3.defaultReferent.name'),
        photoUrl: laurine,
        role: t('messagingV3.defaultReferent.role'),
      },
      isDefault: true,
    };
  }
  return { referent: caregiverIdentity(referentProfile.user, referentProfile), isDefault: false };
}
